import React, { useState, useEffect } from 'react';
import LandingPage from './pages/LandingPage';
import BooksPage from './pages/BooksPage';
import BookDetailPage from './pages/BookDetailPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ContentPackagePage from './pages/ContentPackage';
import PackageDetailPage from './pages/PackageDetailPage';
import BlogsPage from './pages/BlogsPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import OnlineClassesPage from './pages/OnlineClassesPage';
import InPersonClassesPage from './pages/InPersonClassesPage';
import ContactPage from './pages/ContactPage';
import FaqPage from './pages/FaqPage';
import FeedbackPage from './pages/FeedbackPage';
import CheckoutPage from './pages/CheckoutPage';
import UserProfilePage from './pages/UserProfilePage';
import SessionWarningModal from './components/SessionWarningModal';
import authService from './services/authService';
import SessionManager from './services/SessionManager';
import HttpInterceptor from './services/HttpInterceptor';
import useContentProtection from './hooks/useContentProtection';
import './styles/global.css';

type PageType = 'home' | 'books' | 'bookDetail' | 'videoLearning' | 'blogs' | 'blogDetail' | 'faq' | 'feedback' | 'login' | 'admin' | 'onlineClasses' | 'inPersonClasses' | 'contact' | 'checkout' | 'profile' | 'packageDetail';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);
  const [selectedBlogId, setSelectedBlogId] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);
  
  // Session management state
  const [sessionManager, setSessionManager] = useState<SessionManager | null>(null);
  const [showSessionWarning, setShowSessionWarning] = useState<boolean>(false);
  const [sessionMinutesLeft, setSessionMinutesLeft] = useState<number>(0);

  useEffect(() => {
    initializeSessionManagement();
    checkAuthStatus();
    handleUrlParameters();
    
    // Listen for browser back/forward navigation
    const handlePopState = () => {
      handleUrlParameters();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Handle URL parameters for direct navigation
  const handleUrlParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    const id = urlParams.get('id');
    
    if (page === 'bookDetail' && id) {
      setSelectedBookId(id);
      setCurrentPage('bookDetail');
    } else if (page === 'blogDetail' && id) {
      setSelectedBlogId(id);
      setCurrentPage('blogDetail');
    } else if (page === 'packageDetail' && id) {
      setSelectedPackageId(parseInt(id));
      setCurrentPage('packageDetail');
    } else if (page === 'books') {
      setCurrentPage('books');
    } else if (page === 'videoLearning') {
      setCurrentPage('videoLearning');
    } else if (page === 'blogs') {
      setCurrentPage('blogs');
    } else if (page === 'checkout') {
      setCurrentPage('checkout');
    } else if (page === 'profile') {
      setCurrentPage('profile');
    } else if (page === 'faq') {
      setCurrentPage('faq');
    } else if (page === 'feedback') {
      setCurrentPage('feedback');
    } else if (page === 'onlineClasses') {
      setCurrentPage('onlineClasses');
    } else if (page === 'contact') {
      setCurrentPage('contact');
    } else if (window.location.search === '' || page === 'home') {
      // Default page will be set after auth status is checked
      // See checkAuthStatus() function for the actual redirect logic
      setCurrentPage('home');
    }
  };

  // Cleanup session management on unmount
  useEffect(() => {
    return () => {
      if (sessionManager) {
        sessionManager.destroy();
      }
      window.removeEventListener('session-expired', handleSessionExpiredEvent);
    };
  }, [sessionManager]);

  // Content protection - disable copying for non-admin users
  useContentProtection({ disabled: isAdmin || currentPage === 'admin' });

  // Initialize session management
  const initializeSessionManagement = () => {
    const manager = SessionManager.getInstance(
      {
        warningBeforeExpiration: parseInt(process.env.REACT_APP_SESSION_WARNING_MINUTES || '5'), // configurable warning time
        idleTimeout: parseInt(process.env.REACT_APP_SESSION_IDLE_TIMEOUT_MINUTES || '30'), // configurable idle timeout
        checkInterval: parseInt(process.env.REACT_APP_SESSION_CHECK_INTERVAL_SECONDS || '60') // configurable check interval
      },
      {
        onSessionExpired: handleSessionExpired,
        onIdleTimeout: handleIdleTimeout,
        onSessionWarning: handleSessionWarning,
        onSessionExtended: handleSessionExtended
      }
    );

    // Initialize HTTP interceptor
    const httpInterceptor = HttpInterceptor.getInstance();
    httpInterceptor.initialize(manager);

    // Set up session manager in auth service
    authService.setSessionManager(manager);
    
    // Set up auth service in session manager for automatic refresh
    manager.setAuthService(authService);

    // Listen for session expiration events from HTTP interceptor
    window.addEventListener('session-expired', handleSessionExpiredEvent);

    setSessionManager(manager);
    console.log('✅ Session management initialized with automatic refresh');
  };

  // Session event handlers
  const handleSessionExpired = () => {
    console.warn('🔒 Session expired');
    handleLogout('Your session has expired. Please log in again.');
  };

  const handleIdleTimeout = () => {
    console.warn('⏰ Session timed out due to inactivity');
    handleLogout('Your session timed out due to inactivity. Please log in again.');
  };

  const handleSessionWarning = (minutesLeft: number) => {
    console.log(`🔄 Session expiring in ${minutesLeft} minutes - automatic refresh will handle this`);
    // No longer showing popup - automatic refresh will handle expiration
    setSessionMinutesLeft(minutesLeft);
    // setShowSessionWarning(true); // Disabled - using automatic refresh instead
  };

  const handleSessionExtended = () => {
    console.log('🔄 Session extended');
    setShowSessionWarning(false);
  };

  const handleSessionExpiredEvent = (event: any) => {
    console.warn('🔒 Session expired event received:', event.detail);
    const message = event.detail?.message || 'Your session has expired. Please log in again.';
    handleLogout(message);
  };

  const handleExtendSession = () => {
    if (sessionManager) {
      sessionManager.extendSession();
      setShowSessionWarning(false);
      console.log('✅ Session extended by user action');
    }
  };



  const checkAuthStatus = async () => {
    try {
      const authenticated = authService.isAuthenticated();
      console.log('🔍 Auth check - authenticated:', authenticated);
      setIsAuthenticated(authenticated);
      
      if (authenticated) {
        const userData = authService.getUser();
        console.log('👤 User data from localStorage:', userData);
        setUser(userData);
        
        // Check admin status from local user data first
        const isLocalAdmin = userData && (userData.role === 'admin' || userData.user_role === 2);
        console.log('🛡️ Local admin check:', isLocalAdmin, 'user_role:', userData?.user_role);
        
        try {
          // Also verify with server
          const adminCheck = await authService.checkAdminAccess();
          console.log('🔒 Server admin check:', adminCheck);
          setIsAdmin(adminCheck.isAdmin || isLocalAdmin);
        } catch (error) {
          console.warn('Server admin check failed, using local data:', error);
          // Fallback to local admin check if server fails
          setIsAdmin(isLocalAdmin);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
      setIsAdmin(false);
      setUser(null);
    }
  };

  const handleBooksClick = (): void => {
    setCurrentPage('books');
    window.history.pushState(null, '', '?page=books');
  };

  const handleBookDetailClick = (bookId: string): void => {
    setSelectedBookId(bookId);
    setCurrentPage('bookDetail');
  };

  const handlePackageDetailClick = (packageId: number): void => {
    setSelectedPackageId(packageId);
    setCurrentPage('packageDetail');
    window.history.pushState(null, '', `?page=packageDetail&id=${packageId}`);
  };

  const handleBackToPackages = (): void => {
    setCurrentPage('videoLearning');
    setSelectedPackageId(null);
    window.history.pushState(null, '', '?page=videoLearning');
  };

  const handleVideoLearningClick = (): void => {
    setCurrentPage('videoLearning');
    window.history.pushState(null, '', '?page=videoLearning');
  };

  const handleBlogsClick = (): void => {
    setCurrentPage('blogs');
    window.history.pushState(null, '', '?page=blogs');
  };

  const handleFaqClick = (): void => {
    setCurrentPage('faq');
    window.history.pushState(null, '', '?page=faq');
  };

  const handleFeedbackClick = (): void => {
    setCurrentPage('feedback');
    window.history.pushState(null, '', '?page=feedback');
  };

  const handleCheckoutClick = (): void => {
    setCurrentPage('checkout');
    window.history.pushState(null, '', '?page=checkout');
  };

  const handleLoginClick = (): void => {
    setCurrentPage('login');
  };

  const handleAdminClick = (): void => {
    // Check if user is authenticated and has admin role
    if (!isAuthenticated) {
      setCurrentPage('login');
      return;
    }
    
    if (!isAdmin) {
      alert('Access denied. Admin privileges required.');
      return;
    }
    
    setCurrentPage('admin');
  };

  const handleMkAcademyOnlineClick = (): void => {
    setCurrentPage('onlineClasses');
    window.history.pushState(null, '', '?page=onlineClasses');
  };

  const handleMkAcademyInPersonClick = (): void => {
    setCurrentPage('inPersonClasses');
  };

  const handleBackToHome = (): void => {
    setCurrentPage('home');
    window.history.pushState(null, '', '/');
  };

  const handleLoginSuccess = async () => {
    console.log('🎉 Login success - checking user role for redirect...');
    
    // Re-check auth status after successful login
    await checkAuthStatus();
    
    // Get user data to check role
    const userData = authService.getUser();
    console.log('👤 User data after login:', userData);
    
    // Check if user is admin
    const isUserAdmin = userData && (userData.role === 'admin' || userData.user_role === 2);
    console.log('🛡️ Is user admin?', isUserAdmin);
    
    if (isUserAdmin) {
      console.log('🚀 Redirecting admin to admin panel...');
      setCurrentPage('admin');
    } else {
      console.log('📊 Redirecting regular user to dashboard...');
      setCurrentPage('profile'); // Profile page now defaults to dashboard tab
    }
  };

  const handleLogout = async (message?: string) => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    setIsAuthenticated(false);
    setIsAdmin(false);
    setUser(null);
    setCurrentPage('home');
    setShowSessionWarning(false);
    
    if (message) {
      // You could show a toast notification here
      console.log('Logout message:', message);
    }
  };

  const renderPage = (): React.JSX.Element => {
    switch (currentPage) {
      case 'books':
        return (
          <BooksPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onBookDetailClick={handleBookDetailClick}
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'bookDetail':
        return (
          <BookDetailPage 
            bookId={selectedBookId}
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'blogDetail':
        return (
          <BlogDetailPage 
            blogId={selectedBlogId}
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'videoLearning':
        return (
          <ContentPackagePage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onPackageDetailClick={handlePackageDetailClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );

      case 'packageDetail':
        return selectedPackageId ? (
          <PackageDetailPage
            packageId={selectedPackageId}
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onBackToPackages={handleBackToPackages}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        ) : (
          <div style={{ padding: '100px 20px', textAlign: 'center' }}>
            <h2>Package not found</h2>
            <button onClick={handleBackToPackages}>← Back to Packages</button>
          </div>
        );
      case 'blogs':
        return (
          <BlogsPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'faq':
        return (
          <FaqPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onFaqClick={handleFaqClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'login':
        return (
          <LoginPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onLoginSuccess={handleLoginSuccess}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'admin':
        // Double-check admin access before rendering admin page
        if (!isAuthenticated || !isAdmin) {
          return (
            <LoginPage 
              onHomeClick={handleBackToHome} 
              onBooksClick={handleBooksClick} 
              onVideoLearningClick={handleVideoLearningClick} 
              onBlogsClick={handleBlogsClick} 
              onLoginClick={handleLoginClick} 
              onAdminClick={handleAdminClick}
              onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
              onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
              onLoginSuccess={handleLoginSuccess}
              isAuthenticated={isAuthenticated}
              isAdmin={isAdmin}
              user={user}
              onLogout={handleLogout}
            />
          );
        }
        return (
          <AdminPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'onlineClasses':
        return (
          <OnlineClassesPage
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'inPersonClasses':
        return (
          <InPersonClassesPage
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'feedback':
        return (
          <FeedbackPage 
            onHomeClick={handleBackToHome} 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'contact':
        return (
          <ContactPage
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'checkout':
        return (
          <CheckoutPage
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      case 'profile':
        return (
          <UserProfilePage
            onHomeClick={handleBackToHome}
            onBooksClick={handleBooksClick}
            onVideoLearningClick={handleVideoLearningClick}
            onBlogsClick={handleBlogsClick}
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick}
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            onNavigateToPackage={handlePackageDetailClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
      default:
        return (
          <LandingPage 
            onBooksClick={handleBooksClick} 
            onVideoLearningClick={handleVideoLearningClick} 
            onBlogsClick={handleBlogsClick} 
            onFaqClick={handleFaqClick}
            onFeedbackClick={handleFeedbackClick}
            onLoginClick={handleLoginClick} 
            onAdminClick={handleAdminClick}
            onMkAcademyOnlineClick={handleMkAcademyOnlineClick}
            onMkAcademyInPersonClick={handleMkAcademyInPersonClick}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            user={user}
            onLogout={handleLogout}
          />
        );
    }
  };

  return (
    <div className="App">
      {renderPage()}
      
      {/* Session Warning Modal - Disabled: Using automatic refresh instead */}
      {/* 
      <SessionWarningModal
        isOpen={showSessionWarning}
        minutesLeft={sessionMinutesLeft}
        onExtendSession={handleExtendSession}
        onLogout={() => handleLogout('Session ended by user choice.')}
        onClose={() => setShowSessionWarning(false)}
      />
      */}
    </div>
  );
};

export default App; 
